import React, { useState } from 'react';
import {
  Typography,
  Button,
  Grid,
  Paper,
  Box,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const OrderManagement = () => {
  const [previousOrders, setPreviousOrders] = useState([]);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [ordersOnHold, setOrdersOnHold] = useState([]);
  const [visibleSection, setVisibleSection] = useState(null);
  const [loadingSection, setLoadingSection] = useState(null);

  const [negotiationByOrderId, setNegotiationByOrderId] = useState({});
  const [negotiationNotice, setNegotiationNotice] = useState('');

  const history = useHistory();

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const normalizeOrders = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.orders)) return data.orders;
    if (Array.isArray(data?.loads)) return data.loads;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const normalizeThreadsPayload = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.threads)) return data.threads;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const getOrderId = (order) => {
    const raw =
      order?.order_id ||
      order?.id ||
      order?.orderId ||
      order?.order_number ||
      '';

    return String(raw).replace(/^ORD-/i, '').trim();
  };

  const getLatestThreadForOrder = (order) => {
    const orderId = getOrderId(order);
    const payload = negotiationByOrderId[String(orderId)];

    if (!payload) {
      return null;
    }

    const list = normalizeThreadsPayload(payload);

    return list[0] || null;
  };

  const formatMoneyFromCents = (cents) => {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  };

  const hasCustomerNegotiationUpdate = (thread) => {
    if (!thread) {
      return false;
    }

    const lastMessageFromCustomer =
      String(thread.last_message_sender_role || '').toLowerCase() === 'customer';

    const pendingCustomerOffer =
      String(thread.latest_offer_status || '').toUpperCase() === 'PENDING' &&
      String(thread.latest_offer_proposer_role || '').toLowerCase() === 'customer';

    return lastMessageFromCustomer || pendingCustomerOffer;
  };

  const getNegotiationStatusLabel = (thread) => {
    if (!thread) {
      return 'No chat yet';
    }

    const latestOfferStatus = String(thread.latest_offer_status || '').toUpperCase();

    if (hasCustomerNegotiationUpdate(thread)) {
      return 'Customer update';
    }

    if (latestOfferStatus) {
      return latestOfferStatus;
    }

    if (Number(thread.message_count || 0) > 0) {
      return 'Chat';
    }

    return 'Negotiation';
  };

  const getNegotiationStatusColor = (thread) => {
    const latestOfferStatus = String(thread?.latest_offer_status || '').toUpperCase();

    if (hasCustomerNegotiationUpdate(thread)) {
      return {
        backgroundColor: '#fef3c7',
        color: '#92400e',
      };
    }

    if (latestOfferStatus === 'ACCEPTED') {
      return {
        backgroundColor: '#dcfce7',
        color: '#166534',
      };
    }

    if (latestOfferStatus === 'REJECTED') {
      return {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
      };
    }

    if (latestOfferStatus === 'PENDING') {
      return {
        backgroundColor: '#ffedd5',
        color: '#9a3412',
      };
    }

    return {
      backgroundColor: '#e2e8f0',
      color: '#475569',
    };
  };

  const loadNegotiationSummariesForOrders = async (orders) => {
    try {
      if (!token || !Array.isArray(orders) || orders.length === 0) {
        return;
      }

      const entries = await Promise.all(
        orders.map(async (order) => {
          const orderId = getOrderId(order);

          if (!orderId) {
            return null;
          }

          try {
            const response = await fetch(
              `${API}/api/negotiations/order/${orderId}/threads`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (!response.ok) {
              return [String(orderId), { threads: [] }];
            }

            const data = await response.json();
            return [String(orderId), data];
          } catch (error) {
            console.warn(`Unable to load negotiation summary for order ${orderId}:`, error);
            return [String(orderId), { threads: [] }];
          }
        })
      );

      const nextMap = {};

      entries
        .filter(Boolean)
        .forEach(([orderId, data]) => {
          nextMap[orderId] = data;
        });

      setNegotiationByOrderId((previous) => ({
        ...previous,
        ...nextMap,
      }));

      const updatedOrder = orders.find((order) => {
        const orderId = getOrderId(order);
        const threads = normalizeThreadsPayload(nextMap[String(orderId)]);
        return hasCustomerNegotiationUpdate(threads[0]);
      });

      if (updatedOrder) {
        setNegotiationNotice(
          `New customer message or offer on Load #${getOrderId(updatedOrder)}`
        );
      }
    } catch (error) {
      console.warn('Unable to load negotiation summaries:', error);
    }
  };

  const openNegotiationForOrder = async (order) => {
    try {
      const orderId = getOrderId(order);

      if (!orderId) {
        alert('Order id is missing.');
        return;
      }

      if (!token) {
        alert('Your session expired. Please sign in again.');
        return;
      }

      const existingThread = getLatestThreadForOrder(order);
      const existingThreadId =
        existingThread?.id ||
        existingThread?.thread_id ||
        existingThread?.threadId;

      if (existingThreadId) {
        history.push(`/courier/negotiations/thread/${existingThreadId}`);
        return;
      }

      const response = await fetch(
        `${API}/api/negotiations/order/${orderId}/thread`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error(`Open negotiation failed (${response.status})`);
      }

      const thread = await response.json();

      const threadId =
        thread.id ||
        thread.thread_id ||
        thread.threadId ||
        thread.data?.id ||
        thread.data?.thread_id;

      if (!threadId) {
        console.error('Negotiation thread response:', thread);
        throw new Error('Negotiation thread was not returned.');
      }

      history.push(`/courier/negotiations/thread/${threadId}`);
    } catch (error) {
      alert(error.message || 'Unable to open negotiation.');
    }
  };

  const fetchOrders = async (status, setter, sectionKey) => {
    try {
      setLoadingSection(sectionKey);

      const response = await fetch(`${API}/api/orders/${status}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      const list = normalizeOrders(data);

      setter(list);
      setVisibleSection(sectionKey);

      loadNegotiationSummariesForOrders(list);
    } catch (error) {
      console.error(`Error fetching ${status} orders:`, error);
      setter([]);
      setVisibleSection(sectionKey);
    } finally {
      setLoadingSection(null);
    }
  };

  const handleCurrentOrders = () => {
    fetchOrders('accepted', setCurrentOrders, 'current');
  };

  const handleOrdersOnHold = () => {
    fetchOrders('on-hold', setOrdersOnHold, 'on-hold');
  };

  const handlePreviousOrders = () => {
    fetchOrders('previous', setPreviousOrders, 'previous');
  };

  const getStatusColor = (status) => {
    const normalized = String(status || '').toUpperCase();

    if (normalized === 'ACCEPTED') {
      return {
        backgroundColor: '#e0f2fe',
        color: '#0369a1',
      };
    }

    if (normalized === 'DELIVERED' || normalized === 'COMPLETED') {
      return {
        backgroundColor: '#dcfce7',
        color: '#166534',
      };
    }

    if (normalized === 'ON_HOLD' || normalized === 'PENDING') {
      return {
        backgroundColor: '#fef3c7',
        color: '#92400e',
      };
    }

    return {
      backgroundColor: '#e2e8f0',
      color: '#475569',
    };
  };

  const getActiveButtonVariant = (sectionKey) => {
    return visibleSection === sectionKey ? 'contained' : 'outlined';
  };

  const renderEmptyState = () => {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mt: 2,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
          }}
        >
          No loads found.
        </Typography>
      </Paper>
    );
  };

  const renderNegotiationSummary = (order) => {
    const thread = getLatestThreadForOrder(order);
    const customerUpdate = hasCustomerNegotiationUpdate(thread);
    const statusColor = getNegotiationStatusColor(thread);

    if (!thread) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mt: 2,
            backgroundColor: '#f8fafc',
            border: '1px dashed #cbd5e1',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              lineHeight: 1.5,
            }}
          >
            No negotiation messages yet. Open chat to message the customer or review future offers.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mt: 2,
          backgroundColor: customerUpdate ? '#fffbeb' : '#f8fafc',
          border: customerUpdate ? '1px solid #f59e0b' : '1px solid #e2e8f0',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#0f172a',
              fontWeight: 800,
            }}
          >
            Chat / Negotiation
          </Typography>

          <Chip
            label={getNegotiationStatusLabel(thread)}
            size="small"
            sx={{
              ...statusColor,
              fontWeight: 800,
              fontSize: '0.72rem',
            }}
          />
        </Box>

        {thread.order_number && (
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              mb: 0.75,
            }}
          >
            {thread.order_number} · Thread #{thread.id || thread.thread_id}
          </Typography>
        )}

        {thread.last_message_body && (
          <Typography
            variant="body2"
            sx={{
              color: '#475569',
              mb: 0.75,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Latest message: {thread.last_message_body}
          </Typography>
        )}

        {thread.latest_offer_amount_cents && (
          <Typography
            variant="body2"
            sx={{
              color: '#475569',
              mb: 0.75,
            }}
          >
            Latest offer: {formatMoneyFromCents(thread.latest_offer_amount_cents)}
            {thread.latest_offer_proposer_role
              ? ` from ${String(thread.latest_offer_proposer_role).toLowerCase() === 'customer'
                ? 'Customer'
                : 'Driver / Carrier'}`
              : ''}
          </Typography>
        )}

        {thread.latest_offer_notes && (
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Offer notes: {thread.latest_offer_notes}
          </Typography>
        )}
      </Paper>
    );
  };

  const renderOrders = (orders) => {
    if (loadingSection === visibleSection) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mt: 2,
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.95rem',
            }}
          >
            Loading loads...
          </Typography>
        </Paper>
      );
    }

    if (!Array.isArray(orders) || orders.length === 0) {
      return renderEmptyState();
    }

    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {orders.map((order) => {
          const orderId = getOrderId(order);
          const status = String(order.status || 'Unknown').toUpperCase();
          const statusColor = getStatusColor(status);
          const thread = getLatestThreadForOrder(order);
          const customerUpdate = hasCustomerNegotiationUpdate(thread);

          return (
            <Grid item xs={12} key={orderId}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  border: customerUpdate
                    ? '1px solid #f59e0b'
                    : '1px solid rgba(15, 23, 42, 0.08)',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#0f172a',
                        fontWeight: 700,
                        lineHeight: 1.35,
                      }}
                    >
                      Load #{orderId}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '0.92rem',
                      }}
                    >
                      {order.delivery_type || 'Standard'} shipment
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                  >
                    {customerUpdate && (
                      <Chip
                        label="Customer message / offer"
                        size="small"
                        sx={{
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          fontWeight: 800,
                          fontSize: '0.72rem',
                        }}
                      />
                    )}

                    <Chip
                      label={status}
                      size="small"
                      sx={{
                        ...statusColor,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                      }}
                    />

                    <Chip
  label={
    ['paid', 'succeeded'].includes(
      String(order.payment_status || '').toLowerCase()
    )
      ? 'Customer Payment: Paid'
      : String(order.payment_status || '').toLowerCase() === 'pending'
      ? 'Customer Payment: Pending'
      : 'Customer Payment: Not Paid'
  }
  size="small"
  sx={{
    backgroundColor: ['paid', 'succeeded'].includes(
      String(order.payment_status || '').toLowerCase()
    )
      ? '#dcfce7'
      : '#f1f5f9',
    color: ['paid', 'succeeded'].includes(
      String(order.payment_status || '').toLowerCase()
    )
      ? '#166534'
      : '#475569',
    fontWeight: 700,
    fontSize: '0.75rem',
  }}
/>

                  </Box>
                </Box>

                {Number.isSafeInteger(Number(order.agreed_price_cents)) &&
 Number(order.agreed_price_cents) > 0 && (
  <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
      Carrier Earnings
    </Typography>

    <Typography variant="body2">
      Agreed transportation rate: $
      {(Number(order.agreed_price_cents) / 100).toFixed(2)}
    </Typography>

    {order.estimatedCarrierProcessingFeeCents != null &&
     order.estimatedCarrierNetCents != null && (
      <>
        <Typography variant="body2">
          Estimated third-party processing fee: −$
          {(order.estimatedCarrierProcessingFeeCents / 100).toFixed(2)}
        </Typography>

        <Typography variant="body1" sx={{ fontWeight: 700, mt: 1 }}>
          Estimated net earnings: $
          {(order.estimatedCarrierNetCents / 100).toFixed(2)}
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Processing fees are charged by the payment provider,
          not a HaggleFreight brokerage commission or transaction fee.
          Actual processing fees and final payout may differ.
        </Typography>
      </>
    )}
  </Box>
)}

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Pickup
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: '#0f172a',
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                    >
                      {order.from_address || 'Not provided'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Drop-off
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: '#0f172a',
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                    >
                      {order.to_address || 'Not provided'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Service Type
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: '#0f172a',
                        fontSize: '0.95rem',
                      }}
                    >
                      {order.delivery_type || 'Standard'}
                    </Typography>
                  </Grid>

                  {order.distance_in_miles != null && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#64748b',
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Distance
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#0f172a',
                          fontSize: '0.95rem',
                        }}
                      >
                        {order.distance_in_miles} mi
                      </Typography>
                    </Grid>
                  )}

                  {order.weight && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#64748b',
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Weight
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#0f172a',
                          fontSize: '0.95rem',
                        }}
                      >
                        {order.weight}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {renderNegotiationSummary(order)}

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'stretch', sm: 'flex-end' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                  }}
                >
                  <Button
                    variant={customerUpdate ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => {
                      openNegotiationForOrder(order);
                    }}
                    sx={{
                      minHeight: 40,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    {customerUpdate
                      ? 'Open Customer Message / Offer'
                      : 'Open Chat / Negotiation'}
                  </Button>

                  {status === 'ACCEPTED' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        history.push(`/courier/proof-of-delivery/${orderId}`);
                      }}
                      sx={{
                        minHeight: 40,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        width: { xs: '100%', sm: 'auto' },
                      }}
                    >
                      Complete Load
                    </Button>
                  )}

                  {(status === 'DELIVERED' || status === 'COMPLETED') && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        history.push(`/courier/proof-of-delivery-view/${orderId}`);
                      }}
                      sx={{
                        minHeight: 40,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        width: { xs: '100%', sm: 'auto' },
                      }}
                    >
                      View Completion
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          My Loads
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Track active, pending, and completed assignments.
        </Typography>
      </Box>

      {negotiationNotice && (
        <Alert
          severity="info"
          onClose={() => setNegotiationNotice('')}
          sx={{
            mb: 2,
            borderRadius: 2,
          }}
        >
          {negotiationNotice}
        </Alert>
      )}

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <Button
              variant={getActiveButtonVariant('current')}
              color="primary"
              fullWidth
              onClick={handleCurrentOrders}
              sx={{
                minHeight: 42,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Active Loads
            </Button>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              variant={getActiveButtonVariant('on-hold')}
              color="primary"
              fullWidth
              onClick={handleOrdersOnHold}
              sx={{
                minHeight: 42,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Pending Loads
            </Button>
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              variant={getActiveButtonVariant('previous')}
              color="primary"
              fullWidth
              onClick={handlePreviousOrders}
              sx={{
                minHeight: 42,
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Completed Loads
            </Button>
          </Grid>
        </Grid>

        {!visibleSection && (
          <>
            <Divider sx={{ my: 2 }} />

            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '0.95rem',
                }}
              >
                Select a category to view your loads.
              </Typography>
            </Paper>
          </>
        )}

        {visibleSection === 'current' && renderOrders(currentOrders)}
        {visibleSection === 'on-hold' && renderOrders(ordersOnHold)}
        {visibleSection === 'previous' && renderOrders(previousOrders)}
      </Paper>
    </CourierLayout>
  );
};

export default OrderManagement;